
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import STLPreviewBox from "./utilities/STLPreviewBox";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

/* ─────────────────────────────────────────────────────────────────────────────
   Shared anti-bot helpers
───────────────────────────────────────────────────────────────────────────── */

function Spinner({ size = 14 }) {
  const px = typeof size === "number" ? `${size}px` : size;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
      <span
        style={{
          width: px,
          height: px,
          borderRadius: "50%",
          border: "2px solid rgba(15, 23, 42, 0.25)",
          borderTopColor: "rgba(15, 23, 42, 0.95)",
          animation: "spin 0.8s linear infinite",
          display: "inline-block",
        }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </span>
  );
}

async function sha256Hex(str) {
  try {
    if (typeof crypto !== "undefined" && crypto.subtle && typeof TextEncoder !== "undefined") {
      const enc = new TextEncoder().encode(str);
      const buf = await crypto.subtle.digest("SHA-256", enc);
      const arr = Array.from(new Uint8Array(buf));
      return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
    }
  } catch {}
  return sha256HexFallback(str);
}

function sha256HexFallback(ascii) {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let result = "";

  const words = [];
  const asciiBitLength = ascii.length * 8;

  let hash = sha256HexFallback.h || [];
  let k = sha256HexFallback.k || [];
  let primeCounter = k.length;

  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (let i = 0; i < 313; i += candidate) isComposite[i] = candidate;
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }
  sha256HexFallback.h = hash;
  sha256HexFallback.k = k;

  ascii += "\x80";
  while (ascii.length % 64 - 56) ascii += "\x00";

  for (let i = 0; i < ascii.length; i++) {
    const j = ascii.charCodeAt(i);
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (let j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash.slice(0);

    for (let i = 0; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];

      const a = hash[0];
      const e = hash[4];
      const temp1 =
        (hash[7] +
          (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
          ((e & hash[5]) ^ (~e & hash[6])) +
          k[i] +
          (w[i] =
            i < 16
              ? w[i]
              : (w[i - 16] +
                  (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                  w[i - 7] +
                  (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
                0)) |
        0;

      const temp2 =
        ((rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
          ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]))) |
        0;

      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
      hash.pop();
    }

    for (let i = 0; i < 8; i++) hash[i] = (hash[i] + oldHash[i]) | 0;
  }

  for (let i = 0; i < 8; i++) {
    for (let j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? "0" : "") + b.toString(16);
    }
  }
  return result;
}

async function solvePow({ salt, difficulty, abortRef }) {
  const target = "0".repeat(Math.max(0, Number(difficulty) || 0));
  let i = 0;

  while (true) {
    if (abortRef.current) throw new Error("aborted");
    const nonce = String(i);
    const digest = await sha256Hex(`${salt}:${nonce}`);
    if (digest.startsWith(target)) return nonce;
    i += 1;
    if (i % 250 === 0) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }
}

/* ─────────────────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────────────────── */

function uniqStrings(arr) {
  return Array.from(new Set((arr || []).filter((v) => typeof v === "string" && v.trim())));
}

function getDesignDisplayName(name) {
  const raw = String(name || "").trim();
  if (!raw) return "";
  const parts = raw.split("-");
  if (parts.length >= 3) return parts[1] || raw;
  if (parts.length >= 2) return parts[1] || raw;
  return raw;
}

function normalizeProjectFolder(designName) {
  const raw = String(designName || "").trim();
  if (!raw) return "";
  return raw.startsWith("output-") ? raw : `output-${raw}`;
}

function normalizeColorTable(raw) {
  const out = {};
  if (!raw || typeof raw !== "object") return out;

  const toHex = (r, g, b) => {
    const h = (v) =>
      Math.max(0, Math.min(255, Number(v) || 0))
        .toString(16)
        .padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`;
  };

  for (const [name, val] of Object.entries(raw)) {
    const key = String(name).trim().toLowerCase();
    let hex = null;

    if (typeof val === "string") {
      hex = val;
    } else if (Array.isArray(val) && val.length >= 3) {
      hex = toHex(val[0], val[1], val[2]);
    } else if (val && typeof val === "object") {
      const r = val.r ?? val.R ?? val.red;
      const g = val.g ?? val.G ?? val.green;
      const b = val.b ?? val.B ?? val.blue;
      if ([r, g, b].every((v) => Number.isFinite(Number(v)))) {
        hex = toHex(r, g, b);
      }
    }

    if (hex) out[key] = hex;
  }
  return out;
}

function buildThumbnailCandidates(adminName, designName, backendThumbnail) {
  const admin = String(adminName || "").trim();
  const folder = normalizeProjectFolder(designName);

  return uniqStrings([
    backendThumbnail || null,
    admin && folder ? `/outputs/${admin}/${folder}/thumbnail.png` : null,
    admin && folder ? `/outputs/${admin}/${folder}/metadata/thumbnail.png` : null,
  ]);
}

function useResolvedImage(candidates) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [JSON.stringify(candidates)]);

  const src = candidates?.[index] || null;
  const hasValid = index < (candidates?.length || 0);

  return {
    src: hasValid ? src : null,
    onError: () => setIndex((prev) => prev + 1),
  };
}

function hexToRgb(hex) {
  const raw = String(hex || "").replace("#", "").trim();
  if (raw.length !== 6) return null;
  const int = Number.parseInt(raw, 16);
  if (!Number.isFinite(int)) return null;
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function getReadableTextColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#111827";
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.62 ? "#111827" : "#ffffff";
}

function PayPalWordmark() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.02em",
        fontWeight: 800,
        lineHeight: 1,
        flex: "0 0 auto",
        whiteSpace: "nowrap",
      }}
      aria-label="PayPal"
    >
      <span style={{ color: "#003087", fontStyle: "italic" }}>Pay</span>
      <span style={{ color: "#009cde", fontStyle: "italic" }}>Pal</span>
    </span>
  );
}

function AutoFitSingleLine({ children, minFontSize = 11, maxFontSize = 16, style = {} }) {
  const wrapRef = useRef(null);
  const measureRef = useRef(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  useEffect(() => {
    const fit = () => {
      const wrap = wrapRef.current;
      const measure = measureRef.current;
      if (!wrap || !measure) return;

      const available = wrap.clientWidth;
      if (!available) return;

      let next = maxFontSize;
      measure.style.fontSize = `${next}px`;

      while (next > minFontSize && measure.scrollWidth > available) {
        next -= 0.5;
        measure.style.fontSize = `${next}px`;
      }

      setFontSize(next);
    };

    fit();

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && wrapRef.current) {
      ro = new ResizeObserver(() => fit());
      ro.observe(wrapRef.current);
    } else {
      window.addEventListener("resize", fit);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", fit);
    };
  }, [children, minFontSize, maxFontSize]);

  return (
    <span
      ref={wrapRef}
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        minWidth: 0,
        ...style,
      }}
    >
      <span
        aria-hidden="true"
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          whiteSpace: "nowrap",
          left: 0,
          top: 0,
          pointerEvents: "none",
          fontWeight: 700,
        }}
      >
        {children}
      </span>

      <span
        style={{
          display: "inline-flex",
          width: "100%",
          alignItems: "center",
          justifyContent: "center",
          whiteSpace: "nowrap",
          fontSize: `${fontSize}px`,
          lineHeight: 1.1,
        }}
      >
        {children}
      </span>
    </span>
  );
}

async function headExists(url) {
  try {
    const res = await fetch(url, { method: "HEAD", cache: "no-store" });
    return res.ok;
  } catch {
    return false;
  }
}

async function markDesignPaid({ adminName, designName, email, orderID }) {
  const payload = {
    adminName,
    designName,
    email,
    orderID,
  };

  const res = await fetch("/mark-design-paid/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data?.detail || `/mark-design-paid/ failed with ${res.status}`);
  }

  return data;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Viewer
───────────────────────────────────────────────────────────────────────────── */

function Viewer({ urls, colors, onLoadStateChange }) {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !urls || urls.length === 0) {
      onLoadStateChange?.(false);
      return;
    }

    onLoadStateChange?.(true);

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        preserveDrawingBuffer: true,
      });
    } catch (e) {
      console.error("WebGL renderer init failed:", e);
      onLoadStateChange?.(false);
      return;
    }

    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      45,
      mount.clientWidth / mount.clientHeight,
      0.1,
      5000
    );
    camera.position.set(0, 0, 150);

    scene.add(new THREE.AmbientLight(0xffffff, 0.75));

    const key = new THREE.DirectionalLight(0xffffff, 0.95);
    key.position.set(1, 1, 2);
    scene.add(key);

    const fill = new THREE.DirectionalLight(0xffffff, 0.65);
    fill.position.set(-1, -0.5, -1.5);
    scene.add(fill);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    const loader = new STLLoader();
    loader.setCrossOrigin("anonymous");

    const group = new THREE.Group();
    scene.add(group);

    const meshes = [];

    const loadOne = (url, idx) =>
      new Promise((resolve) => {
        loader.load(
          url,
          (geo) => {
            geo.computeVertexNormals();

            const material = new THREE.MeshStandardMaterial({
              color:
                colors?.[idx] ??
                new THREE.Color()
                  .setHSL(((idx * 47) % 360) / 360, 0.55, 0.6)
                  .getHex(),
              metalness: 0.05,
              roughness: 0.9,
              polygonOffset: true,
              polygonOffsetFactor: 1,
              polygonOffsetUnits: idx + 1,
            });

            const mesh = new THREE.Mesh(geo, material);
            mesh.position.z += 0.02 * idx;
            mesh.renderOrder = idx;

            group.add(mesh);
            meshes.push(mesh);
            resolve(true);
          },
          undefined,
          () => resolve(false)
        );
      });

    let frameId = 0;
    let disposed = false;

    const fitScene = () => {
      const bbox = new THREE.Box3().setFromObject(group);
      const sphere = bbox.getBoundingSphere(new THREE.Sphere());
      const radius = sphere.radius || 1;

      const fitOffset = 1.02;
      const fov = THREE.MathUtils.degToRad(camera.fov);
      const dist = (radius / Math.sin(fov / 2)) * fitOffset;
      const tilt = THREE.MathUtils.degToRad(25);

      camera.position.set(
        sphere.center.x + dist * Math.sin(tilt),
        sphere.center.y + dist * Math.sin(tilt),
        sphere.center.z + dist * Math.cos(tilt)
      );
      camera.near = Math.max(0.1, radius / 100);
      camera.far = Math.max(5000, radius * 20);
      camera.lookAt(sphere.center);
      camera.updateProjectionMatrix();

      controls.target.copy(sphere.center);
      controls.update();
      controls.minDistance = dist * 0.25;
      controls.maxDistance = dist * 4;
    };

    const syncSize = () => {
      if (!mount) return;
      const { clientWidth, clientHeight } = mount;
      renderer.setSize(clientWidth, clientHeight);
      camera.aspect = clientWidth / clientHeight || 1;
      camera.updateProjectionMatrix();
    };

    const animate = () => {
      if (disposed) return;
      frameId = requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };

    (async () => {
      try {
        const results = await Promise.all((urls || []).map((u, i) => loadOne(u, i)));
        if (disposed) return;
        if (results.some(Boolean)) fitScene();
        renderer.render(scene, camera);
        onLoadStateChange?.(false);
        animate();
      } catch (err) {
        console.error("Viewer load failed:", err);
        onLoadStateChange?.(false);
      }
    })();

    window.addEventListener("resize", syncSize);

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", syncSize);
      controls.dispose();
      onLoadStateChange?.(false);

      meshes.forEach((m) => {
        m.geometry?.dispose?.();
        if (Array.isArray(m.material)) m.material.forEach((mm) => mm?.dispose?.());
        else m.material?.dispose?.();
      });

      scene.clear();
      renderer.dispose();

      if (renderer.domElement && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [JSON.stringify(urls), JSON.stringify(colors), onLoadStateChange]);

  return <div ref={mountRef} style={{ width: "100%", height: "100%" }} />;
}

/* ─────────────────────────────────────────────────────────────────────────────
   PayPal payment overlay
───────────────────────────────────────────────────────────────────────────── */

function DesignPayPalOverlay({
  email,
  amount,
  currency,
  adminName,
  designName,
  onClose,
}) {
  const [tiersData, setTiersData] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);

  const containerRef = useRef(null);
  const buttonsRef = useRef(null);
  const loadedCurrenciesRef = useRef(new Set());

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/paypal/tiers", { cache: "no-store" });
        if (!res.ok) throw new Error(`tiers ${res.status}`);
        const data = await res.json();
        setTiersData(data);
      } catch (e) {
        console.error("Failed to load PayPal tiers:", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!tiersData || !currency) return;

    const namespace = `paypal_design_${currency}`;
    const scriptId = `paypal-design-sdk-${currency}`;

    setSdkReady(false);

    if (window[namespace]?.Buttons) {
      setSdkReady(true);
      return;
    }

    if (loadedCurrenciesRef.current.has(currency)) {
      const existing = document.getElementById(scriptId);
      if (existing) {
        existing.addEventListener("load", () => setSdkReady(true), { once: true });
      }
      return;
    }

    const s = document.createElement("script");
    s.id = scriptId;
    s.src = `https://www.paypal.com/sdk/js?client-id=${tiersData.paypalClientID}&currency=${currency}&intent=capture&components=buttons`;
    s.async = true;
    s.setAttribute("data-namespace", namespace);

    s.onload = () => {
      loadedCurrenciesRef.current.add(currency);
      setSdkReady(true);
    };
    s.onerror = () => {
      setSdkReady(false);
    };

    document.body.appendChild(s);
  }, [tiersData, currency]);

  useEffect(() => {
    const mount = async () => {
      if (!sdkReady || !containerRef.current || amount == null || !currency) return;

      const namespace = `paypal_design_${currency}`;
      const paypalNS = window[namespace];
      if (!paypalNS || !paypalNS.Buttons) return;

      if (buttonsRef.current) {
        try {
          await buttonsRef.current.close();
        } catch {}
        buttonsRef.current = null;
      }
      containerRef.current.innerHTML = "";

      const buttons = paypalNS.Buttons({
        createOrder: async () => {
          const res = await fetch("/paypal/create-order", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              amount: String(amount),
              currency,
              credits: 1,
              email,
            }),
          });

          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.id) {
            console.error("create-order failed:", data);
            throw new Error("create-order failed");
          }
          return data.id;
        },

        onApprove: async (data) => {
          if (isFinalizing) return;

          try {
            setIsFinalizing(true);

            const res = await fetch("/paypal/capture-order", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                orderID: data.orderID,
                email,
                credits: 1,
              }),
            });

            const payload = await res.json().catch(() => ({}));
            if (!res.ok) {
              console.error("capture-order failed:", payload);
              alert("Payment capture failed.");
              setIsFinalizing(false);
              return;
            }

            await markDesignPaid({
              adminName,
              designName,
              email,
              orderID: data.orderID,
            });

            onCloseRef.current?.();
            window.location.reload();
          } catch (err) {
            console.error("Payment finalize error:", err);
            alert(err?.message || "Something went wrong with the PayPal payment.");
            setIsFinalizing(false);
          }
        },

        onError: (err) => {
          console.error("PayPal onError:", err);
          alert("Something went wrong with the PayPal payment.");
        },

        onCancel: () => {},
        style: { layout: "vertical", height: 45 },
      });

      if (!buttons.isEligible()) {
        containerRef.current.innerHTML =
          '<div style="padding:8px;color:#b91c1c">PayPal buttons are not eligible here.</div>';
        return;
      }

      await buttons.render(containerRef.current);
      buttonsRef.current = buttons;
    };

    mount();
  }, [sdkReady, amount, currency, email, adminName, designName, isFinalizing]);

  useEffect(() => {
    return () => {
      try {
        buttonsRef.current?.close?.();
      } catch {}
      buttonsRef.current = null;
      if (containerRef.current) containerRef.current.innerHTML = "";
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        style={{
          position: "relative",
          background: "white",
          width: "520px",
          maxWidth: "92vw",
          maxHeight: "85vh",
          overflowY: "auto",
          borderRadius: "12px",
          padding: "2vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <button
          onClick={() => !isFinalizing && onCloseRef.current?.()}
          style={{
            position: "absolute",
            top: 10,
            right: 14,
            background: "transparent",
            border: "none",
            fontSize: "1.2rem",
            cursor: isFinalizing ? "not-allowed" : "pointer",
            opacity: isFinalizing ? 0.5 : 1,
          }}
          aria-label="Close"
          disabled={isFinalizing}
        >
          ✖
        </button>

        <h3 style={{ marginBottom: 0, fontSize: "1.6rem", color: "#111827" }}>
          Complete Payment
        </h3>

        <div
          style={{
            width: "80%",
            padding: "10px 12px",
            borderRadius: 8,
            backgroundColor: "#e2e8f0",
            color: "#1e293b",
            fontWeight: "bold",
            fontSize: "1rem",
            textAlign: "center",
          }}
        >
          {amount} {currency}
        </div>

        {isFinalizing && (
          <div
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              background: "#f8fafc",
              color: "#334155",
              textAlign: "center",
              fontWeight: 600,
            }}
          >
            Finalizing payment...
          </div>
        )}

        <div
          ref={containerRef}
          style={{ marginTop: "0.5rem", width: "100%", minHeight: 60, opacity: isFinalizing ? 0.65 : 1 }}
        />
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Access overlay
───────────────────────────────────────────────────────────────────────────── */

function DesignAccessOverlay({
  adminName,
  designName,
  designDisplayName,
  thumbnailCandidates,
  onVerified,
}) {
  const [emailInput, setEmailInput] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState("");

  const [antiBotChecked, setAntiBotChecked] = useState(false);
  const [antiBotBusy, setAntiBotBusy] = useState(false);
  const [antiBotPassed, setAntiBotPassed] = useState(false);
  const [antiBotToken, setAntiBotToken] = useState(null);

  const [challenge, setChallenge] = useState(null);
  const [checking, setChecking] = useState(false);

  const abortPowRef = useRef(false);
  const thumb = useResolvedImage(thumbnailCandidates);

  useEffect(() => {
    let isMounted = true;

    async function loadChallenge() {
      try {
        const cRes = await fetch("/anti-bot/challenge", { cache: "no-store" });
        const cData = await cRes.json();
        if (!isMounted) return;
        if (cRes.ok && cData?.challengeId && cData?.salt) setChallenge(cData);
        else setChallenge(null);
      } catch {
        if (isMounted) setChallenge(null);
      }
    }

    loadChallenge();
    return () => {
      isMounted = false;
      abortPowRef.current = true;
    };
  }, []);

  const refreshChallenge = async () => {
    try {
      const cRes = await fetch("/anti-bot/challenge", { cache: "no-store" });
      const cData = await cRes.json();
      if (cRes.ok && cData?.challengeId && cData?.salt) {
        setChallenge(cData);
        return cData;
      }
    } catch {}
    setChallenge(null);
    return null;
  };

  const resetAntiBot = () => {
    abortPowRef.current = true;
    abortPowRef.current = false;
    setAntiBotChecked(false);
    setAntiBotBusy(false);
    setAntiBotPassed(false);
    setAntiBotToken(null);
    setError("");
  };

  const handleAntiBotToggle = async (checked) => {
    setError("");

    if (!checked) {
      resetAntiBot();
      return;
    }

    const localChallenge = challenge || (await refreshChallenge());
    if (!localChallenge) {
      setError("Anti-bot system unavailable. Please refresh.");
      return;
    }

    abortPowRef.current = false;
    setAntiBotChecked(true);
    setAntiBotBusy(true);
    setAntiBotPassed(false);
    setAntiBotToken(null);

    try {
      const nonce = await solvePow({
        salt: localChallenge.salt,
        difficulty: localChallenge.difficulty,
        abortRef: abortPowRef,
      });

      if (abortPowRef.current) throw new Error("aborted");

      const vRes = await fetch("/anti-bot/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          challengeId: localChallenge.challengeId,
          nonce,
        }),
      });

      const vData = await vRes.json();

      if (!vRes.ok || !vData?.ok || !vData?.token) {
        throw new Error(vData?.detail ? JSON.stringify(vData.detail) : "Verify failed");
      }

      setAntiBotToken(vData.token);
      setAntiBotBusy(false);
      setAntiBotPassed(true);
    } catch (e) {
      console.error("Anti-bot failed:", e);
      setAntiBotBusy(false);
      setAntiBotPassed(false);
      setAntiBotChecked(false);
      setAntiBotToken(null);
      setError("Anti-bot check failed. Please try again.");
      await refreshChallenge();
    }
  };

  const handleVerifyEmail = async () => {
    setError("");

    if (!antiBotPassed || !antiBotToken) {
      setError("Please complete the Anti-bot check first.");
      return;
    }
    if (!emailInput.trim()) {
      setError("Please enter your email.");
      return;
    }

    setChecking(true);

    try {
      const res = await fetch(`/auth/eligibility?email=${encodeURIComponent(emailInput.trim())}`, {
        method: "GET",
        headers: { "X-AntiBot-Token": antiBotToken },
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.detail || "Eligibility check failed.");
      if (!data?.eligible) {
        throw new Error("Wabushinator Usage is only available for my Patreon followers!");
      }

      const verifiedPayload = {
        email: emailInput.trim(),
        antiBotToken,
        ts: Date.now(),
      };

      try {
        sessionStorage.setItem(
          `design-access:${adminName}:${designName}`,
          JSON.stringify(verifiedPayload)
        );
      } catch {}

      onVerified?.(verifiedPayload);
    } catch (e) {
      console.error("design eligibility error", e);
      setError(e?.message || "Verification failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const canVerify = antiBotPassed && !!emailInput.trim() && !checking;

  return (
    <div style={overlayStyles.backdrop}>
      <div style={overlayStyles.modal}>
        <h2 style={overlayStyles.title}>Custom Design Request</h2>

        <div style={overlayStyles.thumbnailPanel}>
          {thumb.src ? (
            <img
              src={thumb.src}
              alt={designDisplayName || designName}
              style={overlayStyles.thumbnail}
              onError={thumb.onError}
            />
          ) : (
            <div style={overlayStyles.thumbnailPlaceholder}>No thumbnail</div>
          )}
        </div>

        <div style={overlayStyles.designTag}>
          <div>
            <strong>Creator:</strong> {adminName}
          </div>
          <div>
            <strong>Design:</strong> {designDisplayName || designName}
          </div>
        </div>

        <input
          type="text"
          name="company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-10000px",
            top: "auto",
            width: "1px",
            height: "1px",
            overflow: "hidden",
          }}
        />

        <label style={overlayStyles.checkboxRow}>
          <input
            type="checkbox"
            checked={antiBotChecked}
            onChange={(e) => handleAntiBotToggle(e.target.checked)}
            disabled={checking || antiBotBusy}
            style={{ transform: "scale(1.1)" }}
          />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span>I'm not a bot</span>
            {antiBotBusy ? <Spinner size={14} /> : antiBotPassed ? <span style={{ opacity: 0.85 }}>✅</span> : null}
          </span>
        </label>

        <input
          type="email"
          value={emailInput}
          placeholder={antiBotPassed ? "Enter your Email" : "Check box to unlock"}
          onChange={(e) => {
            setEmailInput(e.target.value);
            setError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canVerify) handleVerifyEmail();
          }}
          disabled={!antiBotPassed || checking}
          style={{
            width: "100%",
            marginBottom: "12px",
            padding: "0.7rem",
            textAlign: "center",
            border: "1px solid #cbd5e1",
            borderRadius: "8px",
            boxSizing: "border-box",
            backgroundColor: antiBotPassed && !checking ? "#fff" : "#f1f5f9",
            cursor: antiBotPassed && !checking ? "text" : "not-allowed",
          }}
        />

        <button
          onClick={handleVerifyEmail}
          disabled={!canVerify}
          style={{
            width: "100%",
            padding: "0.7rem 1rem",
            backgroundColor: canVerify ? "#1d4ed8" : "#93c5fd",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: canVerify ? "pointer" : "not-allowed",
            fontWeight: 700,
          }}
        >
          {checking ? "Verifying..." : "Verify Email"}
        </button>

        {error && <p style={overlayStyles.error}>{error}</p>}

        <div style={overlayStyles.footerNote}>
          Please check the box and enter the email address you are subscribed with.
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main page
───────────────────────────────────────────────────────────────────────────── */

export default function DesignPage({ adminName, designName }) {
  const [loading, setLoading] = useState(false);
  const [designData, setDesignData] = useState(null);
  const [error, setError] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [overlayBackendThumbnail, setOverlayBackendThumbnail] = useState(null);

  const [colorMap, setColorMap] = useState({});
  const [colorsForUi, setColorsForUi] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [previewColors, setPreviewColors] = useState([]);
  const [viewerLoading, setViewerLoading] = useState(false);

  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const designDisplayName = useMemo(() => getDesignDisplayName(designName), [designName]);
  const projectFolder = useMemo(() => normalizeProjectFolder(designName), [designName]);
  const projectRoot = useMemo(() => {
    const admin = String(adminName || "").trim();
    return admin && projectFolder ? `/outputs/${admin}/${projectFolder}` : "";
  }, [adminName, projectFolder]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch("/color_table.json", { cache: "no-cache" });
        if (!res.ok) throw new Error("Missing /color_table.json");
        const data = await res.json();
        if (!cancelled) setColorMap(normalizeColorTable(data));
      } catch {
        try {
          const res2 = await fetch("/color-table/", { cache: "no-cache" });
          if (res2.ok && !cancelled) {
            const data2 = await res2.json();
            setColorMap(normalizeColorTable(data2));
          }
        } catch {}
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadOverlayPreview() {
      if (!adminName || !designName) return;

      try {
        const res = await fetch(
          `/design-data/${encodeURIComponent(adminName)}/${encodeURIComponent(designName)}`,
          { cache: "no-store" }
        );

        const contentType = res.headers.get("content-type") || "";
        const data = contentType.includes("application/json") ? await res.json() : null;

        if (!res.ok) return;
        if (isMounted) setOverlayBackendThumbnail(data?.design?.thumbnail || null);
      } catch {
        if (isMounted) setOverlayBackendThumbnail(null);
      }
    }

    loadOverlayPreview();
    return () => {
      isMounted = false;
    };
  }, [adminName, designName]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(`design-access:${adminName}:${designName}`);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (parsed?.email && parsed?.antiBotToken) {
        setVerifiedEmail(parsed.email);
        setIsVerified(true);
      }
    } catch {}
  }, [adminName, designName]);

  useEffect(() => {
    let isMounted = true;

    async function loadDesign() {
      if (!isVerified) return;

      try {
        if (!adminName || !designName) throw new Error("Missing design route parameters.");

        setLoading(true);
        setError("");

        const res = await fetch(
          `/design-data/${encodeURIComponent(adminName)}/${encodeURIComponent(designName)}`,
          { cache: "no-store" }
        );

        const contentType = res.headers.get("content-type") || "";
        const data = contentType.includes("application/json") ? await res.json() : null;

        if (!res.ok) throw new Error(data?.detail || "Failed to load design page.");
        if (isMounted) setDesignData(data);
      } catch (err) {
        if (isMounted) setError(err.message || "Failed to load design.");
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadDesign();
    return () => {
      isMounted = false;
    };
  }, [adminName, designName, isVerified]);

  useEffect(() => {
    if (!projectRoot || !isVerified) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(`${projectRoot}/metadata.json?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return;
        const meta = await res.json();

        const normPath = (p) =>
          String(p || "")
            .replace(/^\.\//, "")
            .replace(/\/+/g, "/")
            .trim()
            .toLowerCase();

        const pickColorName = (p) => {
          if (!p || typeof p !== "string") return "";
          const mm = p.match(/(?:^|\/)\s*\d+\s*-\s*([^./]+)\.(?:svg|png)$/i);
          return mm && mm[1] ? mm[1].trim() : "";
        };

        const baseSvgRel = String(meta?.base_svg || "").replace(/^\.\//, "").trim();
        const baseSvgNorm = normPath(baseSvgRel);
        let base = pickColorName(baseSvgRel);

        let rest = Array.isArray(meta.colors)
          ? meta.colors
              .filter((e) => e && (e.file || e.name))
              .filter((e) => String(e.name).toLowerCase() !== "base")
              .filter((e) => {
                const fileNorm = normPath(e.file);
                return !(fileNorm && baseSvgNorm && fileNorm === baseSvgNorm);
              })
              .map((e) => (e.file ? pickColorName(e.file) : String(e.name || "")))
              .filter(Boolean)
          : [];

        if (!base && Array.isArray(meta.colors)) {
          const baseObj = meta.colors.find((c) => String(c?.name).toLowerCase() === "base");
          if (baseObj?.file) base = pickColorName(baseObj.file);
          if (!base && baseObj?.name) base = String(baseObj.name).trim();
        }

        const parsed = [base, ...rest].filter(Boolean);
        if (!cancelled) setColorsForUi(parsed);
      } catch (e) {
        console.warn("[DesignPage] metadata color hydrate failed", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectRoot, isVerified]);

  const overlayThumbnailCandidates = useMemo(
    () => buildThumbnailCandidates(adminName, designName, overlayBackendThumbnail),
    [adminName, designName, overlayBackendThumbnail]
  );

  const nameToHex = useCallback(
    (name) => {
      if (!name) return null;
      const key = String(name).trim().toLowerCase();
      return colorMap[key] || null;
    },
    [colorMap]
  );

  const colorChips = useMemo(
    () =>
      (colorsForUi || []).map((name) => {
        const hex = nameToHex(name) || "#cbd5e1";
        return {
          name,
          hex,
          textColor: getReadableTextColor(hex),
        };
      }),
    [colorsForUi, nameToHex]
  );

  useEffect(() => {
    if (!projectRoot || colorsForUi.length === 0) {
      setPreviewUrls([]);
      setPreviewColors([]);
      setViewerLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      const urlColorPairs = [];

      const blackUrl = `${projectRoot}/stls/EMSparts/0 - Black.stl`;
      if (await headExists(blackUrl)) {
        urlColorPairs.push({
          url: blackUrl,
          color: nameToHex("Black") || "#111111",
        });
      }

      for (let i = 0; i < colorsForUi.length; i++) {
        const c = colorsForUi[i];
        const url = `${projectRoot}/stls/EMSparts/${i + 1} - ${c}.stl`;
        if (await headExists(url)) {
          urlColorPairs.push({
            url,
            color: nameToHex(c) || null,
          });
        }
      }

      if (!cancelled) {
        setPreviewUrls(urlColorPairs.map((x) => x.url));
        setPreviewColors(urlColorPairs.map((x) => x.color));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectRoot, colorsForUi, nameToHex]);

  const has3dPreview = previewUrls.length > 0;
  const design = designData?.design || {};
  const hasPaymentData =
    design.price !== undefined &&
    design.price !== null &&
    String(design.Currency || "").trim() !== "";


    const handleDownload = async () => {
    if (isDownloading) return;

    try {
        setIsDownloading(true);

        const response = await fetch("/download/", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/zip",
        },
        body: JSON.stringify({
            name: designName,
            email: adminName,
        }),
        });

        if (!response.ok) {
        console.error("Download failed:", await response.text());
        throw new Error("ZIP file not found");
        }

        const blob = await new Promise((resolve) =>
        response.blob().then((b) => resolve(b))
        );

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${designDisplayName || designName}_grouped.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (e) {
        console.error("Download failed:", e);
        alert(e?.message || "Download failed.");
    } finally {
        setIsDownloading(false);
    }
    };

  return (
    <div style={styles.page}>
      {!isVerified && (
        <DesignAccessOverlay
          adminName={adminName}
          designName={designName}
          designDisplayName={designDisplayName}
          thumbnailCandidates={overlayThumbnailCandidates}
          onVerified={(payload) => {
            setVerifiedEmail(payload?.email || "");
            setIsVerified(true);
          }}
        />
      )}

      {!isVerified ? null : loading ? (
        <div style={styles.statusBlock}>Loading design...</div>
      ) : error ? (
        <div style={styles.statusBlock}>
          <h2 style={styles.title}>Design not available</h2>
          <p style={styles.errorText}>{error}</p>
        </div>
      ) : (
        <div style={styles.contentWrap}>
          <div style={styles.topSection}>
            <div style={styles.previewWrap}>
              <div className="design-page-preview-suppressor">
                <style>{`
                  .design-page-preview-suppressor > div > div:first-child {
                    display: none !important;
                  }
                `}</style>

                <STLPreviewBox
                  hasGenerated={has3dPreview}
                  showExtrusionOverlayPanel={false}
                  setShowExtrusionOverlayPanel={() => {}}
                  colorsForUi={[]}
                  extrusionValues={[]}
                  controlsLocked={true}
                  stepExtrusion={() => {}}
                  handleExtrusionChange={() => {}}
                  handleExtrusionBlur={() => {}}
                  makeItFlat={false}
                  setMakeItFlat={() => {}}
                  makeItFlatDisabled={true}
                  processedImage={null}
                  overlayImage={null}
                >
                  <div style={styles.viewerStage}>
                    {has3dPreview ? (
                      <Viewer
                        urls={previewUrls}
                        colors={previewColors}
                        onLoadStateChange={setViewerLoading}
                      />
                    ) : null}

                    {viewerLoading && (
                      <div style={styles.renderingOverlay}>
                        <img
                          src="/rendering.gif"
                          alt="Rendering..."
                          style={styles.renderingGif}
                        />
                      </div>
                    )}
                  </div>
                </STLPreviewBox>
              </div>

              <div style={styles.previewHelpText}>
                Use the interactive window to rotate and zoom in the 3D Model.
              </div>
            </div>

            <div style={styles.infoWrap}>
              <h1 style={styles.title}>{designDisplayName || designName}</h1>

              <div style={styles.metaRow}>
                <span style={styles.metaLabel}>Creator:</span>
                <span>{designData?.adminName || adminName}</span>
              </div>

              <div style={{ ...styles.metaRow, alignItems: "flex-start" }}>
                <span style={styles.metaLabel}>Colors:</span>
                <div style={styles.colorsSection}>
                  <div style={styles.colorsWrap}>
                    {colorChips.length > 0 ? (
                      colorChips.map((chip) => (
                        <span
                          key={chip.name}
                          title={chip.name}
                          style={{
                            ...styles.colorChip,
                            background: chip.hex,
                            color: chip.textColor,
                            borderColor:
                              chip.hex.toLowerCase() === "#ffffff" ? "#cbd5e1" : "transparent",
                          }}
                        >
                          {chip.name}
                        </span>
                      ))
                    ) : (
                      <span style={styles.mutedText}>No colors found</span>
                    )}
                  </div>

                  {design.paid === true && (
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={isDownloading}
                      style={{
                        ...styles.downloadButton,
                        opacity: isDownloading ? 0.7 : 1,
                        cursor: isDownloading ? "not-allowed" : "pointer",
                      }}
                    >
                      {isDownloading ? "Preparing Download..." : "Download Zipped 3D Files"}
                    </button>
                  )}

                  {design.paid === false && hasPaymentData && (
                    <button
                      type="button"
                      onClick={() => setShowPaymentOverlay(true)}
                      style={styles.paypalButton}
                    >
                      <AutoFitSingleLine minFontSize={10.5} maxFontSize={16}>
                        <span style={styles.paypalButtonLine}>
                          <span style={{ whiteSpace: "nowrap" }}>Pay with</span>
                          <PayPalWordmark />
                          <span style={{ whiteSpace: "nowrap" }}>— {design.price} {design.Currency}</span>
                        </span>
                      </AutoFitSingleLine>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {design.description && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Description</h3>
              <p style={styles.description}>{design.description}</p>
            </div>
          )}
        </div>
      )}

      {showPaymentOverlay && design.paid === false && hasPaymentData && (
        <DesignPayPalOverlay
          email={verifiedEmail}
          amount={design.price}
          currency={design.Currency}
          adminName={adminName}
          designName={designName}
          onClose={() => setShowPaymentOverlay(false)}
        />
      )}
    </div>
  );
}

const overlayStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
    zIndex: 999999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    boxSizing: "border-box",
  },
  modal: {
    backgroundColor: "#ffffff",
    padding: "2rem",
    borderRadius: "12px",
    width: "90vw",
    maxWidth: "420px",
    boxShadow: "0 0 20px rgba(0,0,0,0.35)",
    textAlign: "center",
    position: "relative",
    color: "#0f172a",
  },
  title: {
    marginTop: 0,
    marginBottom: "14px",
    fontSize: "1.7rem",
  },
  thumbnailPanel: {
    width: "100%",
    marginBottom: "14px",
  },
thumbnailPanel: {
  width: "100%",
  marginBottom: "14px",
  aspectRatio: "1 / 1",
  borderRadius: "12px",
  border: "1px solid #dbe2ea",
  background: "#f8fafc",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},

thumbnail: {
  width: "100%",
  height: "100%",
  display: "block",
  objectFit: "contain",
  borderRadius: "12px",
  background: "#f8fafc",
},

thumbnailPlaceholder: {
  width: "100%",
  height: "100%",
  borderRadius: "12px",
  background: "#f8fafc",
  color: "#94a3b8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
},
  designTag: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "16px",
    fontSize: "13px",
    lineHeight: 1.6,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    margin: "0 auto 0.9rem auto",
    fontSize: 14,
    color: "#0f172a",
    userSelect: "none",
  },
  error: {
    color: "red",
    marginTop: "1rem",
    textAlign: "center",
    fontSize: "14px",
  },
  footerNote: {
    marginTop: "12px",
    fontSize: "12px",
    color: "#475569",
    lineHeight: 1.5,
  },
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#e2e8f0",
    color: "#000000",
    padding: "32px",
    boxSizing: "border-box",
  },
  statusBlock: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  contentWrap: {
    maxWidth: "1400px",
    margin: "0 auto",
  },
  topSection: {
    display: "flex",
    gap: "24px",
    alignItems: "flex-start",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  previewWrap: {
    flex: "0 1 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  viewerStage: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  renderingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(17, 17, 17, 0.2)",
    pointerEvents: "none",
  },
  renderingGif: {
    width: "28%",
    minWidth: "84px",
    maxWidth: "150px",
    height: "auto",
    objectFit: "contain",
    filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.35))",
  },
  previewHelpText: {
    marginTop: "12px",
    fontSize: "14px",
    color: "#000000",
    textAlign: "center",
    maxWidth: "81vmin",
  },
  infoWrap: {
    flex: "1 1 320px",
    minWidth: "260px",
    maxWidth: "520px",
  },
  title: {
    marginTop: 0,
    marginBottom: "18px",
    fontSize: "2rem",
    lineHeight: 1.2,
  },
  metaRow: {
    display: "flex",
    gap: "8px",
    marginBottom: "12px",
    fontSize: "1.05rem",
  },
  metaLabel: {
    fontWeight: "bold",
    minWidth: "70px",
  },
  colorsSection: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    alignItems: "flex-start",
    width: "100%",
  },
  colorsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "center",
  },
  colorChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "34px",
    minHeight: "34px",
    padding: "6px 10px",
    borderRadius: "4px",
    fontSize: "12px",
    fontWeight: 700,
    border: "1px solid transparent",
    boxSizing: "border-box",
    textTransform: "capitalize",
  },
  mutedText: {
    color: "#cbd5e1",
    fontSize: "14px",
  },
  section: {
    marginTop: "28px",
  },
  sectionTitle: {
    marginBottom: "12px",
  },
  description: {
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
  },
  paypalButton: {
    padding: "14px 18px",
    borderRadius: "10px",
    border: "none",
    background: "#ffc439",
    color: "#111827",
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "min(100%, 420px)",
    minWidth: 0,
    overflow: "hidden",
  },
  paypalButtonLine: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.45em",
    width: "100%",
    minWidth: 0,
    flexWrap: "nowrap",
  },
  downloadButton: {
    padding: "14px 24px",
    borderRadius: "10px",
    border: "none",
    background: "#93c5fd",
    color: "#111827",
    fontWeight: 700,
    fontSize: "16px",
  },
  errorText: {
    color: "#ff8080",
  },
};
